import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
    Row, Col, Typography, Table, Button, Input, Modal,
    Form, Flex, Popconfirm, Space, Tabs, Radio, Card, Upload,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
    getActivities, createActivity, updateActivity, removeActivity,
    getKunden, createKunde, updateKunde, removeKunde, importKunden,
    getSettings, updateSettings,
} from './redux/slice';

const { Title, Text } = Typography;

// ── Tätigkeiten Tab ──────────────────────────────────────────────────────────
const TaetigkeitenTab = ({ organizer, t }) => {
    const dispatch = useDispatch();
    const { activities, activityLoading } = useSelector(s => s.supportTracking);
    const [form] = Form.useForm();
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);

    useEffect(() => {
        if (organizer) dispatch(getActivities({ organizer, search: '' }));
    }, [organizer]);

    const openAdd = () => { setEditRecord(null); form.resetFields(); setModalOpen(true); };
    const openEdit = (r) => { setEditRecord(r); form.setFieldsValue({ name: r.name }); setModalOpen(true); };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const result = editRecord
                ? await dispatch(updateActivity({ id: editRecord._id, name: values.name, organizer }))
                : await dispatch(createActivity({ name: values.name, organizer }));
            if (result?.payload) setModalOpen(false);
        } catch (_) {}
    };

    const columns = [
        { title: t('support_taetigkeit_name'), dataIndex: 'name', key: 'name' },
        {
            title: '', key: 'actions', width: 100,
            render: (_, r) => (
                <Space>
                    <Button icon={<EditOutlined />} size='small' onClick={() => openEdit(r)} />
                    <Popconfirm title={t('support_delete_confirm')} onConfirm={() => dispatch(removeActivity({ id: r._id, organizer }))} okText={t('organizer_common_yes')} cancelText={t('organizer_common_cancelButton')}>
                        <Button icon={<DeleteOutlined />} size='small' danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Flex vertical gap={16}>
            <Row justify='space-between' align='middle'>
                <Col>
                    <Row gutter={8}>
                        <Col><Input placeholder={t('organizer_common_search')} value={search} onChange={e => setSearch(e.target.value)} onPressEnter={() => dispatch(getActivities({ organizer, search }))} allowClear /></Col>
                        <Col><Button type='primary' onClick={() => dispatch(getActivities({ organizer, search }))}>{t('organizer_common_search')}</Button></Col>
                    </Row>
                </Col>
                <Col><Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>{t('support_taetigkeit_hinzufuegen')}</Button></Col>
            </Row>
            <div className='bg-white rounded-2'>
                <Table dataSource={activities} columns={columns} rowKey='_id' loading={activityLoading} pagination={{ pageSize: 20 }} />
            </div>
            <Modal open={modalOpen} title={<Title level={4} type='success'>{editRecord ? t('support_taetigkeit_bearbeiten') : t('support_taetigkeit_hinzufuegen')}</Title>} footer={null} onCancel={() => setModalOpen(false)}>
                <Form form={form} layout='vertical'>
                    <Form.Item name='name' label={t('support_taetigkeit_name')} rules={[{ required: true, message: t('support_taetigkeit_name') + ' ' + t('organizer_common_isrequired') }]}>
                        <Input placeholder='z.B. Fernwartung' />
                    </Form.Item>
                    <Row justify='end' gutter={8}>
                        <Col><Button onClick={() => setModalOpen(false)}>{t('organizer_common_cancelButton')}</Button></Col>
                        <Col><Button type='primary' loading={activityLoading} onClick={handleSave}>{t('organizer_common_saveButton')}</Button></Col>
                    </Row>
                </Form>
            </Modal>
        </Flex>
    );
};

// ── Kundenliste Tab ──────────────────────────────────────────────────────────
const KundenlisteTab = ({ organizer, t }) => {
    const dispatch = useDispatch();
    const { kunden, kundenLoading } = useSelector(s => s.supportTracking);
    const [form] = Form.useForm();
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [csvText, setCsvText] = useState('');
    const fileRef = useRef(null);

    useEffect(() => {
        if (organizer) dispatch(getKunden({ organizer, search: '' }));
    }, [organizer]);

    const openAdd = () => { setEditRecord(null); form.resetFields(); setModalOpen(true); };
    const openEdit = (r) => { setEditRecord(r); form.setFieldsValue({ kundenId: r.kundenId, kundenName: r.kundenName }); setModalOpen(true); };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const result = editRecord
                ? await dispatch(updateKunde({ id: editRecord._id, ...values, organizer }))
                : await dispatch(createKunde({ ...values, organizer }));
            if (result?.payload) setModalOpen(false);
        } catch (_) {}
    };

    const handleFileRead = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => setCsvText(e.target.result);
        reader.readAsText(file, 'UTF-8');
        return false;
    };

    const handleImport = async () => {
        if (!csvText) return;
        const result = await dispatch(importKunden({ organizer, csv: csvText }));
        if (result?.payload) { setImportModalOpen(false); setCsvText(''); }
    };

    const columns = [
        { title: t('support_kundenId'), dataIndex: 'kundenId', key: 'kundenId', width: 160 },
        { title: t('support_kundenName'), dataIndex: 'kundenName', key: 'kundenName' },
        {
            title: '', key: 'actions', width: 100,
            render: (_, r) => (
                <Space>
                    <Button icon={<EditOutlined />} size='small' onClick={() => openEdit(r)} />
                    <Popconfirm title={t('support_delete_confirm')} onConfirm={() => dispatch(removeKunde({ id: r._id, organizer }))} okText={t('organizer_common_yes')} cancelText={t('organizer_common_cancelButton')}>
                        <Button icon={<DeleteOutlined />} size='small' danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Flex vertical gap={16}>
            <Row justify='space-between' align='middle'>
                <Col>
                    <Row gutter={8}>
                        <Col><Input placeholder={t('organizer_common_search')} value={search} onChange={e => setSearch(e.target.value)} onPressEnter={() => dispatch(getKunden({ organizer, search }))} allowClear /></Col>
                        <Col><Button type='primary' onClick={() => dispatch(getKunden({ organizer, search }))}>{t('organizer_common_search')}</Button></Col>
                    </Row>
                </Col>
                <Col>
                    <Space>
                        <Button icon={<UploadOutlined />} onClick={() => setImportModalOpen(true)}>{t('support_kunden_importieren')}</Button>
                        <Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>{t('support_kunde_hinzufuegen')}</Button>
                    </Space>
                </Col>
            </Row>
            <div className='bg-white rounded-2'>
                <Table dataSource={kunden} columns={columns} rowKey='_id' loading={kundenLoading} pagination={{ pageSize: 20 }} />
            </div>

            {/* Add/Edit Modal */}
            <Modal open={modalOpen} title={<Title level={4} type='success'>{editRecord ? t('support_kunde_bearbeiten') : t('support_kunde_hinzufuegen')}</Title>} footer={null} onCancel={() => setModalOpen(false)}>
                <Form form={form} layout='vertical'>
                    <Form.Item name='kundenId' label={t('support_kundenId')} rules={[{ required: true, message: t('support_kundenId') + ' ' + t('organizer_common_isrequired') }]}>
                        <Input placeholder='z.B. K-10042' />
                    </Form.Item>
                    <Form.Item name='kundenName' label={t('support_kundenName')}>
                        <Input placeholder='z.B. Mustermann GmbH' />
                    </Form.Item>
                    <Row justify='end' gutter={8}>
                        <Col><Button onClick={() => setModalOpen(false)}>{t('organizer_common_cancelButton')}</Button></Col>
                        <Col><Button type='primary' loading={kundenLoading} onClick={handleSave}>{t('organizer_common_saveButton')}</Button></Col>
                    </Row>
                </Form>
            </Modal>

            {/* Import Modal */}
            <Modal open={importModalOpen} title={<Title level={4} type='success'>{t('support_kunden_importieren')}</Title>} footer={null} onCancel={() => { setImportModalOpen(false); setCsvText(''); }}>
                <Flex vertical gap={16}>
                    <Text type='secondary'>{t('support_import_hint')}</Text>
                    <Upload beforeUpload={handleFileRead} accept='.csv,.txt' showUploadList={false}>
                        <Button icon={<UploadOutlined />}>{t('support_csv_waehlen')}</Button>
                    </Upload>
                    {csvText && <Text type='success'>✓ {t('support_csv_geladen')}</Text>}
                    <Row justify='end' gutter={8}>
                        <Col><Button onClick={() => { setImportModalOpen(false); setCsvText(''); }}>{t('organizer_common_cancelButton')}</Button></Col>
                        <Col><Button type='primary' loading={kundenLoading} disabled={!csvText} onClick={handleImport}>{t('support_importieren')}</Button></Col>
                    </Row>
                </Flex>
            </Modal>
        </Flex>
    );
};

// ── Zeitrundung Tab ──────────────────────────────────────────────────────────
const ZeitrundungTab = ({ organizer, t }) => {
    const dispatch = useDispatch();
    const { settings, settingsLoading } = useSelector(s => s.supportTracking);
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (organizer) dispatch(getSettings(organizer));
    }, [organizer]);

    useEffect(() => {
        setValue(settings?.roundingMinutes ?? 0);
    }, [settings]);

    const handleSave = () => {
        dispatch(updateSettings({ organizer, roundingMinutes: value }));
    };

    return (
        <Flex vertical gap={24} style={{ maxWidth: 500 }}>
            <Card>
                <Flex vertical gap={16}>
                    <Text strong>{t('support_rundung_beschreibung')}</Text>
                    <Radio.Group value={value} onChange={e => setValue(e.target.value)}>
                        <Flex vertical gap={10}>
                            <Radio value={0}>{t('support_rundung_keine')}</Radio>
                            <Radio value={5}>5 {t('support_rundung_minuten')}</Radio>
                            <Radio value={10}>10 {t('support_rundung_minuten')}</Radio>
                            <Radio value={15}>15 {t('support_rundung_minuten')}</Radio>
                            <Radio value={20}>20 {t('support_rundung_minuten')}</Radio>
                            <Radio value={30}>30 {t('support_rundung_minuten')}</Radio>
                        </Flex>
                    </Radio.Group>
                    <Row justify='end'>
                        <Button type='primary' loading={settingsLoading} onClick={handleSave}>{t('organizer_common_saveButton')}</Button>
                    </Row>
                </Flex>
            </Card>
        </Flex>
    );
};

// ── Main Page ────────────────────────────────────────────────────────────────
const SupportEinstellungenPage = () => {
    const { auth } = useSelector(s => s);
    const { t } = useTranslation();
    const organizer = auth?.decodeToken?.organizer;

    const items = [
        { key: '1', label: t('support_taetigkeiten'), children: <TaetigkeitenTab organizer={organizer} t={t} /> },
        { key: '2', label: t('support_kundenliste'), children: <KundenlisteTab organizer={organizer} t={t} /> },
        { key: '3', label: t('support_zeitrundung'), children: <ZeitrundungTab organizer={organizer} t={t} /> },
    ];

    return (
        <Fragment>
            <Flex vertical gap={16}>
                <Row className='padding-1 rounded-1'>
                    <Col><Title level={4} type='success'>{t('support_einstellungen_title')}</Title></Col>
                </Row>
                <div className='bg-white rounded-2' style={{ padding: '16px 24px' }}>
                    <Tabs items={items} />
                </div>
            </Flex>
        </Fragment>
    );
};

export default SupportEinstellungenPage;
